---
title: "Account Setup, Verification and Deposits in USDT"
slug: account-setup-deposits
shortTitle: "Account Setup & Deposits"
description: "Step by step: create your {{BRAND}} account, verify your identity, answer the short questionnaire and fund your account in USDT by crypto, wire or card."
level: beginner
category: trading
course: platform-guide
order: 1
topics: [security, stablecoins]
publishedAt: 2026-10-19
updatedAt: 2026-10-19
featured: false
popular: false
cover: /academy/img/covers/account-setup-deposits.svg
faqs:
  - q: "How long does identity verification take?"
    a: "Once you send your documents, your account shows as under review. Allow {{KYC_REVIEW_TIME}}; unclear or mismatched documents are the most common reason it takes longer."
  - q: "Which documents can I use to verify my identity?"
    a: "A passport, national ID card or driver's license (front and back), a selfie of you holding that same document, and a proof of address dated within the last 90 days. Files can be JPG, PNG or PDF, up to 10 MB each."
  - q: "Which USDT network should I choose for a crypto deposit?"
    a: "Choose one of the networks offered on the Deposit page (TRC20, ERC20 or BEP20) that your sending wallet or exchange also supports. The network you send on must match the network of the deposit address exactly."
  - q: "What happens if I send USDT on the wrong network?"
    a: "Funds sent on a network that does not match the deposit address may never arrive and can be lost. Contact support from Account → Support straight away with the transaction details, but recovery is not always possible."
  - q: "Can I deposit in US dollars, euros or pounds?"
    a: "Yes. The Wire transfer and Card tabs on the Deposit page let you request a deposit in USD, GBP or EUR. Your trading account itself is kept in USDT."
  - q: "Why am I being asked about the source of my funds?"
    a: "Above a certain deposit level, {{BRAND}} may ask where your money comes from as part of its account checks. If it does, the request appears under Personal Information on the Source of funds tab."
---

Before you can place a real trade on {{BRAND}}, you need an account, a verified identity and some money in it. The process happens in your browser or on your phone and follows a fixed order: sign up, add your details, upload your documents, wait for the review, answer a short questionnaire, then deposit.

Your account is kept in USDT, a stablecoin designed to track the US dollar. Balances are shown with a $ sign because 1 USDT is treated as $1. This guide walks through each screen and points out the details that most often slow people down. If USDT is new to you, read [What Is USDT](/academy/article/what-is-usdt) first.

<CTA label="Create your account" to="/register" />

<KeyTakeaways>

- **Setup follows a fixed order.** Sign up, add your details, verify your identity, wait for the review, answer the questionnaire, then deposit.
- **Clear documents mean a faster review.** Uncropped photos, matching names and a proof of address no more than 90 days old avoid most rejections.
- **Your account runs in USDT.** Every market is settled in USDT, and balances show a $ sign because 1 USDT is treated as $1.
- **Match the network on crypto deposits.** USDT sent on a different network from the deposit address can be lost.

</KeyTakeaways>

## What you need before you start

Gather these first and you can finish in one sitting:

- An email address you can open right away, for the sign-up code.
- Your mobile number.
- A valid passport, national ID card or driver's license.
- A proof of address dated within the last 90 days, showing your name and the address you will enter.
- A phone or webcam for a selfie holding your ID.

Files can be JPG, PNG or PDF, up to 10 MB each.

{/* TODO: verify with platform — the list of accepted proof-of-address documents (for example utility bill, bank statement). */}

## Step 1: Create your account

<Steps>
<Step title="Sign up with your email">

Go to [the sign-up page](/register) and enter your email address. {{BRAND}} emails you a code; type it in to confirm the address is yours.

</Step>
<Step title="Set a strong password">

Choose a password you do not use anywhere else. Passwords need at least 8 characters, including an upper-case letter, a lower-case letter and a digit; a longer passphrase is stronger still.

{/* TODO: verify with platform — where the password is set during sign-up, and whether the same rules apply as for password changes. */}

</Step>
<Step title="Plan to switch on 2FA">

Two-factor authentication (2FA) adds a second check, a code from an authenticator app, when you log in. Switch it on under **Account → Personal information → Security**. [Security Basics](/academy/article/security-basics) explains why it matters.

</Step>
</Steps>

## Step 2: Add your personal details

Onboarding first asks for your legal name, date of birth, nationality, country of residence, address and mobile number. Enter your name exactly as it appears on your ID, including middle names, and your address exactly as it appears on your proof of address. Small mismatches are one of the most common reasons documents are sent back.

## Step 3: Verify your identity

Next you upload three things. This identity check is often called KYC, short for "know your customer".

<CompareTable caption="Verification documents and how to get them right">

| Document | What to upload | Common reasons for rejection |
| --- | --- | --- |
| Photo ID | Front and back of a passport, ID card or driver's license | Cropped corners, glare, expired document |
| Selfie | You holding the same ID next to your face | Face or ID unreadable, a different ID used |
| Proof of address | A document dated within 90 days | Too old, name or address does not match |

</CompareTable>

<Screenshot name="kyc-upload-documents" alt="The identity verification step with upload areas for ID front, ID back, selfie and proof of address" caption="Verify identity: upload your ID, a selfie holding it and a recent proof of address." />

<ProTip>

Photograph documents on a dark, flat surface in daylight, with all four corners in the frame and no flash. If a photo is hard for you to read, it will be hard for the reviewer too.

</ProTip>

Once everything is uploaded, your account shows as under review. Allow {{KYC_REVIEW_TIME}}. If a document is rejected, you can usually replace just that document.

{/* TODO: verify with platform — how a rejected document is reported (email, KYC tab) and how to re-upload it. */}

Above a certain deposit level you may also be asked about the source of your funds. That request appears under **Personal Information** on the **Source of funds** tab.

## Step 4: Answer the trading-experience questionnaire

Before your first deposit, {{BRAND}} asks a short set of questions about your trading knowledge and experience. Answer honestly. The questions help check whether leveraged products are appropriate for you, and there is nothing to gain from overstating your experience. CFDs are high-risk, and most traders lose money trading them.

## Step 5: Deposit funds

Open the Deposit page with the **Deposit** button in the top bar, from the account menu, or at [Account → Deposit](/account/deposit). It has three tabs: **Crypto**, **Wire transfer** and **Card**. Each deposit must be at least {{MIN_DEPOSIT}}.

### Depositing USDT by crypto

The **Crypto** tab offers two routes: a payment page that shows an address, a QR code and a timer, or a USDT deposit address on one of three networks, TRC20, ERC20 or BEP20.

<Steps>
<Step title="Choose the network">

Pick the network your sending wallet or exchange will use. The deposit address belongs to that network only.

</Step>
<Step title="Copy the address or scan the QR code">

Use the copy button or the QR code rather than typing the address. Check the first and last few characters in your wallet before you send.

</Step>
<Step title="Send from your wallet or exchange">

Select the same network on the sending side. On the payment page, send before the timer runs out.

</Step>
<Step title="Wait for the deposit to arrive">

The blockchain must confirm the transfer before your balance is credited. The amount then appears in USDT in your Wallet and in the client bar.

</Step>
</Steps>

<Screenshot name="deposit-crypto-tab" alt="The Crypto tab of the Deposit page showing the amount to send, the network, the address with copy buttons, the QR code and the time left" caption="Deposit → Crypto: copy the address or scan the QR code, and send the exact amount before the timer runs out." />

{/* TODO: verify with platform — confirmations needed per network, and what happens to a payment sent after the payment-page timer expires. */}

<CompareTable caption="The three USDT deposit networks">

| Network | Blockchain | Address starts with | Watch out for |
| --- | --- | --- | --- |
| TRC20 | Tron | T | Your exchange may call it "Tron (TRC20)" |
| ERC20 | Ethereum | 0x | Looks identical to a BEP20 address |
| BEP20 | BNB Smart Chain | 0x | Looks identical to an ERC20 address |

</CompareTable>

<Warning>

Always send on the same network as the deposit address. ERC20 and BEP20 addresses look exactly alike, so a wallet may let you send on the wrong one without complaint, and the funds can be lost. For a first deposit, consider sending a small test amount first.

</Warning>

<Example title="Network fees on a first deposit (illustrative numbers)">

You want to deposit 500 USDT from an exchange over TRC20. Say your exchange charges a 1 USDT network fee and deducts it from the amount you send.

- You send 500 USDT, so 500 − 1 = **499 USDT** arrives.
- To have a full 500 USDT arrive, send 500 + 1 = **501 USDT**.

Make sure the amount that arrives, not the amount you send, meets the minimum. The fee here is charged by the exchange, not by {{BRAND}}.

</Example>

### Wire transfer and card

The **Wire transfer** and **Card** tabs create a deposit request in USD, GBP or EUR. Follow the instructions on the tab and keep any reference it gives you, because a bank transfer without the right reference can take longer to match to your account. Your trading balance is held in USDT.

{/* TODO: verify with platform — the wire and card request flow, the payment reference, processing times and how USD, GBP and EUR deposits are converted to USDT. */}

## If something goes wrong

- **The sign-up code has not arrived.** Check your spam folder and that the email address was typed correctly.
- **Verification is taking longer than expected.** Look at the **KYC** tab under **Personal Information** in case a document needs replacing.
- **A crypto deposit has not appeared.** Find the transaction ID (hash) in your sending wallet and check it on a block explorer. If it is confirmed but still missing, contact support from **Account → Support** with the network, amount and transaction ID.

Support staff will never ask for your password or 2FA codes, so never share them, whoever asks.

## After your first deposit

Your balance shows on the Wallet page and in the client bar on the trade screen. Before trading with it, consider practicing first: the **Demo** button in the desktop top bar gives you {{DEMO_BALANCE}} to practice with on the same screens, without real money.

{/* TODO: verify with platform — demo availability and how it is requested. */}

When you are ready, the [Platform Tour](/academy/article/platform-tour) explains every part of the trade screen.

## Conclusion

Setting up a {{BRAND}} account comes down to five steps: sign up with your email, add your details, upload your ID, selfie and proof of address, answer the questionnaire, and deposit. Most delays come from mismatched names, blurry photos or old address documents, and most lost deposits come from choosing the wrong network. Take a minute over each of those, switch on 2FA, and only deposit money you can afford to lose.
